import { ReactNode } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CircleHelp } from 'lucide-react';

import { Label as LabelUI } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  TOOLTIP_ARROW_CLASS_NAME,
  TOOLTIP_BUBBLE_CLASS_NAME,
} from '@/components/ui/tooltip';

import { cn } from '@/lib/utils';

type FieldHintProps = {
  hint: ReactNode;
};

export const FieldHint = (props: FieldHintProps) => {
  const { hint } = props;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-subtle-foreground hover:bg-accent focus-visible:border-ring focus-visible:text-ring focus-visible:bg-accent data-[state=open]:bg-accent data-[state=open]:border-ring data-[state=open]:text-ring flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-[2px] border border-transparent outline-none"
        >
          <CircleHelp className="size-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        sideOffset={6}
        className={cn(
          TOOLTIP_BUBBLE_CLASS_NAME,
          'max-w-60 font-normal shadow-none',
        )}
      >
        {hint}
        <PopoverPrimitive.Arrow className={cn(TOOLTIP_ARROW_CLASS_NAME)} />
      </PopoverContent>
    </Popover>
  );
};

type InputLabelProps = {
  htmlFor?: string;
  label: ReactNode;
  hint?: ReactNode;
  isInvalid?: boolean;
  isRequired?: boolean;
};

export const InputLabel = (props: InputLabelProps) => {
  const { htmlFor, label, hint, isInvalid, isRequired } = props;

  return (
    <div className="flex items-center gap-1">
      <LabelUI
        htmlFor={htmlFor}
        data-slot="input-label"
        className={cn(
          'text-sm gap-1 font-medium',
          isInvalid
            ? 'text-destructive-soft-foreground'
            : 'text-subtle-foreground',
        )}
      >
        {label}
        {isRequired && <span aria-hidden="true">*</span>}
      </LabelUI>
      {hint && <FieldHint hint={hint} />}
    </div>
  );
};
